import React, {
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react';
import {
  ControlButton,
  ReactFlowProvider,
  reconnectEdge,
  useEdgesState,
  useNodesState,
  useReactFlow,
  type Connection,
  type Edge,
  type EdgeChange,
  type Node,
  type NodeChange,
  type NodeTypes,
  type FinalConnectionState,
} from '@xyflow/react';
import {
  useWatchableValue,
  useActionsContext,
} from '@cotera/client/v0/actions/framework';
import { Redo2, Undo2 } from 'lucide-react';
import { useArtifactPanel } from '@cotera/client/v0/chat/artifact-panel-context';
import { toast } from '@cotera/client/app/components/ui';
import { Canvas } from '@cotera/client/v0/components/ai-elements/canvas';
import {
  WORKFLOW_FLOW_NODE_TYPE,
  WORKFLOW_FLOW_TRIGGER_TYPE,
  WorkflowFlowNode,
  WorkflowFlowTrigger,
  type WorkflowFlowNodeData,
  type WorkflowFlowTriggerData,
} from './components';
import { useWorkflowModel } from './workflow.model';
import { useWorkflowGraphMutations } from './use-workflow-graph-mutations';
import { useWorkflowRenderCatalog } from './workflow-render-catalog';
import { syncWorkflowSelectionTargetModel } from './sync-workflow-selection-target';
import type { DrillTarget } from './workflow-drill-context';
import { workflowTreeViewEntry } from './workflow-artifact-views';
import {
  WorkflowAddNodeFromSourceProvider,
  type WorkflowAddNodeFromSource,
} from './components/workflow-add-node-context';
import { WorkflowOpenNodeConfigProvider } from './components/workflow-open-node-config-context';
import { WorkflowRefIssuesProvider } from './components/workflow-ref-issues-context';
import { useWorkflowRefIssues } from './hooks/use-workflow-ref-issues';
import { WorkflowStructureIssuesProvider } from './components/workflow-structure-issues-context';
import { useWorkflowStructureIssues } from './hooks/use-workflow-structure-issues';
import { WorkflowEditorToolbar } from './workflow-editor-toolbar';
import {
  emptyWorkflowFlowGraph,
  isSyntheticWorkflowEdgeId,
  isWorkflowTriggerFlowNodeId,
  workflowGraphToFlow,
  workflowNodeViewDataFromGraphNode,
} from './workflow-graph-to-flow';
import { WORKFLOW_FLOW_FIT_VIEW_OPTIONS } from './workflow-flow-layout.constants';
import {
  buildFlowMeasurementKey,
  useMeasuredFlowLayout,
} from './hooks/use-measured-flow-layout';
import { applyWorkflowFlowManualPositions } from './workflow-flow-manual-positions';
import {
  connectFromForDroppedSource,
  resolveConnectEnd,
} from './workflow-connect-end-add-node';
import { useWorkflowAddNodeActions } from './use-workflow-add-node-actions';
import { resolveWorkflowNodeEditStrategy } from './components/node/edit-strategies';
import { WorkflowEditorInspector } from './workflow-editor-inspector';
import { useRevealNodeBesideInspector } from './hooks/use-reveal-node-beside-inspector';
import { workflowTriggerFlowNodeId } from './workflow-flow-node-ids';
import { workflowInspectorTargetIsLive } from './workflow-inspector-target';
import { useConfirmWorkflowDelete } from './workflow-delete-confirm';
import {
  WORKFLOW_EDGE_OPTIONS,
  litNodeIds,
  useSelectedNodeIds,
  withSelectedEdgeHighlight,
  withUnselectedNodesDimmed,
} from './workflow-edge-style';

const WORKFLOW_NODE_TYPES: NodeTypes = {
  [WORKFLOW_FLOW_NODE_TYPE]: WorkflowFlowNode,
  [WORKFLOW_FLOW_TRIGGER_TYPE]: WorkflowFlowTrigger,
};

// How long the "Test webhook" affordance runs: every edge animates and the
// toolbar button reads "Testing…" for exactly this long, then both reset
// together. Test runs execute async (watch them in the Runs panel) — this is just
// a clear "we fired it" indicator, not a live run tracker.
const TEST_ANIMATION_MS = 3000;

const ACTIVE_TEST_EDGE_STYLE: React.CSSProperties = {
  stroke: 'var(--primary)',
  strokeWidth: 1.5,
  strokeDasharray: '6 6',
};

// While testing, every edge animates (dashes march) and tints primary.
function animateTestEdges(edges: Edge[]): Edge[] {
  return edges.map((edge) => ({
    ...edge,
    animated: true,
    style: ACTIVE_TEST_EDGE_STYLE,
  }));
}

/**
 * Carry the current selection onto a rebuilt node/edge array.
 *
 * `workflowGraphToFlow` builds fresh objects with no `selected` flag, so syncing
 * the graph into React Flow state used to silently clear the selection. Any graph
 * notification landing just after a click — an autosaving editor committing on
 * mount, a debounced save completing — took the highlight with it, and the node
 * needed clicking a second time.
 *
 * Selection is view state, not graph state; a graph sync has no business
 * overwriting it.
 */
function withPreservedSelection<T extends { id: string; selected?: boolean }>(
  current: readonly T[],
  next: T[]
): T[] {
  const selectedIds = new Set(
    current.filter((item) => item.selected === true).map((item) => item.id)
  );
  if (selectedIds.size === 0) {
    return next;
  }
  return next.map((item) =>
    selectedIds.has(item.id) ? { ...item, selected: true } : item
  );
}

/**
 * Hand the selection to the nodes that were just pasted, so it is obvious what
 * landed where — a paste re-runs the layout, and the copies do not necessarily
 * appear beside their originals.
 *
 * Not every pasted id shows up here: duplicating a for-each copies its loop
 * body too, and loop interiors are deliberately absent from the canvas. So this
 * selects whichever copies are visible, and returns null only when none of them
 * are (nothing to highlight).
 */
function withPastedSelection<T extends { id: string; selected?: boolean }>(
  next: T[],
  pastedIds: readonly string[]
): T[] | null {
  const pasted = new Set(pastedIds);
  if (!next.some((item) => pasted.has(item.id))) {
    return null;
  }
  return next.map((item) => ({ ...item, selected: pasted.has(item.id) }));
}

/** Re-fit when the laid-out graph changes so fitView does not leave a ~2× zoom on small graphs. */
const WorkflowFitView: React.FC<{ layoutKey: string }> = ({ layoutKey }) => {
  const { fitView } = useReactFlow();

  useEffect(() => {
    if (layoutKey === '') {
      return;
    }
    const frame = requestAnimationFrame(() => {
      void fitView(WORKFLOW_FLOW_FIT_VIEW_OPTIONS);
    });
    return () => {
      cancelAnimationFrame(frame);
    };
  }, [layoutKey, fitView]);

  return null;
};

/**
 * Sizing for the undo/redo icons in the zoom-controls bar. React Flow caps
 * control-button svgs at 12px and fills them (`fill: currentColor`) — tuned for
 * its own bold built-in icons. lucide icons are stroke-based and lighter, so at
 * 12px they read as smaller than the native +/−/fit/lock. Lift the cap and size
 * a touch larger so the glyph weight matches, and switch fill→stroke.
 */
const HISTORY_ICON_CLASS =
  'size-[15px]! max-w-none! max-h-none! fill-none! stroke-current!';

/** `⌘` on macOS, `Ctrl+` elsewhere — for the shortcut hint in the tooltip. */
const HISTORY_SHORTCUT_MOD =
  typeof navigator !== 'undefined' &&
  /Mac|iP(hone|ad|od)/.test(navigator.userAgent)
    ? '⌘'
    : 'Ctrl+';

/**
 * Undo / Redo appended to React Flow's bottom-left zoom-controls cluster (via the
 * Canvas `controlsSlot`) — the persistent canvas-utility corner, where these
 * conventionally live (vs the top action bar, which is for adding/editing). Each
 * is disabled when there is nothing to undo/redo; the tooltip (native `title`)
 * teaches the shortcut. Clicking drives the model directly; the keyboard +
 * command palette go through WorkflowUndo/RedoAction.
 */
const WorkflowHistoryControls: React.FC = () => {
  const model = useWorkflowModel();
  const canUndo = useWatchableValue(model.canUndoWatchable);
  const canRedo = useWatchableValue(model.canRedoWatchable);

  return (
    <>
      <ControlButton
        onClick={() => model.undo()}
        disabled={canUndo === false}
        title={`Undo (${HISTORY_SHORTCUT_MOD}Z)`}
        aria-label="Undo"
        className="disabled:opacity-40 disabled:hover:bg-transparent!"
      >
        <Undo2 className={HISTORY_ICON_CLASS} strokeWidth={2.25} />
      </ControlButton>
      <ControlButton
        onClick={() => model.redo()}
        disabled={canRedo === false}
        title={`Redo (${HISTORY_SHORTCUT_MOD}⇧Z)`}
        aria-label="Redo"
        className="disabled:opacity-40 disabled:hover:bg-transparent!"
      >
        <Redo2 className={HISTORY_ICON_CLASS} strokeWidth={2.25} />
      </ControlButton>
    </>
  );
};

const WorkflowEditorCanvasInner: React.FC<{
  toolbarSlot: HTMLDivElement | null;
}> = ({ toolbarSlot }) => {
  const model = useWorkflowModel();
  const graph = useWatchableValue(model.graphWatchable);
  const catalog = useWorkflowRenderCatalog();
  const actionsContext = useActionsContext();
  const mutations = useWorkflowGraphMutations();
  const { openAddNodeModal } = useWorkflowAddNodeActions();
  // Node and trigger config opens in the floating inspector, so the graph stays
  // on screen. The panel is still used to drill into full pages the inspector
  // links out to (the nested-workflow tree; the dataset behind a dataset
  // trigger, opened from its inspector's "Open dataset" button).
  const artifactPanel = useArtifactPanel();
  const isHydratingRef = useRef(false);
  const isReconnectingRef = useRef(false);
  const manualPositionsRef = useRef(
    new Map<string, { x: number; y: number }>()
  );
  // Node/trigger config lives in the floating inspector, so the graph stays on
  // screen while you edit it.
  const [inspectorTarget, setInspectorTarget] = useState<DrillTarget>(null);
  const revealNode = useRevealNodeBesideInspector();

  const openConfig = useCallback(
    (target: NonNullable<DrillTarget>, flowNodeId: string) => {
      // Nothing to configure and no page to drill into (a run-input node, an
      // unrecognised type) — let the click stay a plain selection rather than
      // opening a panel that only apologises for being empty. Every trigger —
      // including a dataset trigger, which edits in place and offers its own
      // "Open dataset" button — opens in the inspector, so the graph stays on
      // screen and the node stays selectable (and therefore deletable).
      if (
        target.kind === 'node' &&
        resolveWorkflowNodeEditStrategy(target.node.type) === null
      ) {
        return;
      }
      // No expanding here on purpose — the layout stays exactly as the user left
      // it. The inspector adapts instead, docking along the bottom while chat is
      // alongside and to the side once the artifact is expanded.
      setInspectorTarget(target);
      revealNode(flowNodeId);
    },
    [revealNode]
  );

  const closeInspector = useCallback(() => {
    setInspectorTarget(null);
  }, []);

  // Open a node's config by id, even when it has no card on the canvas. The only
  // caller today is the for-each editor reaching its hidden loop body so an
  // attached tool call can be configured. Builds the same view-model
  // `workflowGraphToFlow` stamps on a card, so the inspector renders identically.
  const openNodeConfigById = useCallback(
    (nodeId: string) => {
      if (graph === null) {
        return;
      }
      const node = graph.nodes.find((row) => row.id === nodeId);
      if (node === undefined) {
        return;
      }
      openConfig(
        { kind: 'node', node: workflowNodeViewDataFromGraphNode(node) },
        node.id
      );
    },
    [graph, openConfig]
  );

  const [isTesting, setIsTesting] = useState(false);

  // The nested-workflow tree takes over the panel, exactly as opening a single
  // nested workflow does — but it does *not* expand it. The tree is a graph that
  // fits itself to whatever width it is given, same as this canvas, so it works
  // alongside the chat; resizing the layout under someone who only clicked a
  // toolbar button would be gratuitous. (Contrast the dataset trigger's "Open
  // dataset" button, which expands because it pushes a *page* that wants width.)
  const openNestedTree = useMemo(() => {
    if (artifactPanel === null) {
      return undefined;
    }
    return () => {
      artifactPanel.pushView(
        workflowTreeViewEntry({ workflowId: model.workflowId }),
        { replaceTop: true }
      );
    };
  }, [artifactPanel, model.workflowId]);

  const graphFlow = useMemo(() => {
    if (graph === null) {
      return emptyWorkflowFlowGraph();
    }
    return workflowGraphToFlow(graph);
  }, [graph]);

  // Deleting what you were editing closes its config panel with it, rather than
  // leaving a form open over a node that no longer exists (and whose Save would
  // have nothing to write to).
  //
  // Keyed on the graph rather than hung off the delete handlers so every route
  // that can remove a node lands here alike: the Delete key, the toolbar button,
  // a trigger unbound from its card, an edit Coco makes from chat. Deletes are
  // draft-state updates, so the graph has already dropped the row by the time
  // React Flow removes the card — the panel closes in the same beat.
  useEffect(() => {
    if (graph === null) {
      return;
    }
    // Close only when the edited thing is truly gone. A node is checked against
    // the full graph (not just the visible flow) so a for-each loop body, hidden
    // from the canvas but editable in the inspector, isn't closed the instant it
    // opens; a trigger is checked against the flow, where it lives. See
    // `workflowInspectorTargetIsLive`.
    const graphNodeIds = new Set(graph.nodes.map((node) => node.id));
    const flowNodeIds = new Set(graphFlow.nodes.map((node) => node.id));
    if (
      workflowInspectorTargetIsLive(inspectorTarget, graphNodeIds, flowNodeIds)
    ) {
      return;
    }
    setInspectorTarget(null);
  }, [graph, graphFlow.nodes, inspectorTarget]);

  const displayEdges = useMemo(() => {
    if (!isTesting) {
      return graphFlow.edges;
    }
    return animateTestEdges(graphFlow.edges);
  }, [graphFlow.edges, isTesting]);

  const handleTestStarted = useCallback(() => {
    setIsTesting(true);
    model.setEditorTab('runs');
  }, [model]);

  const graphStructureKey = useMemo(
    () =>
      [
        graphFlow.nodes.map((node) => node.id).join(','),
        graphFlow.edges.map((edge) => edge.id).join(','),
      ].join('|'),
    [graphFlow.nodes, graphFlow.edges]
  );

  const [nodes, setNodes, onNodesChange] = useNodesState<Node>(graphFlow.nodes);
  const [edges, setEdges, onEdgesChange] = useEdgesState<Edge>(graphFlow.edges);

  // Selecting a node lights up its incoming and outgoing arrows too, so a step's
  // place in the graph stays readable. Render-time only: the highlight must not be
  // written back into edge state.
  const selectedNodeIds = useSelectedNodeIds(nodes);
  // Everything the selection touches stays lit; the rest of the graph fades, so a
  // selected step reads as a path rather than a card with a ring around it.
  const litIds = useMemo(
    () => litNodeIds(edges, selectedNodeIds),
    [edges, selectedNodeIds]
  );
  const renderNodes = useMemo(
    () => withUnselectedNodesDimmed(nodes, litIds),
    [nodes, litIds]
  );
  // Same deal for the just-pasted ring: derived for the render, never stored.
  const renderEdges = useMemo(
    () => withSelectedEdgeHighlight(edges, selectedNodeIds),
    [edges, selectedNodeIds]
  );

  const measurementKey = useMemo(() => buildFlowMeasurementKey(nodes), [nodes]);

  useMeasuredFlowLayout({
    graphStructureKey,
    measurementKey,
    layoutEdges: graphFlow.edges,
    manualPositions: manualPositionsRef.current,
    setNodes,
  });

  // Drop positions for nodes that are gone, and keep the rest.
  //
  // This used to clear the whole map on any structural change, which meant
  // adding, deleting or duplicating one node re-dagre'd every other node out
  // from under wherever the user had dragged it. Keeping them can leave a
  // hand-placed node overlapping a new neighbour — visible, and one drag to fix,
  // where a full reshuffle is neither.
  useEffect(() => {
    const live = new Set(graphFlow.nodes.map((node) => node.id));
    for (const nodeId of [...manualPositionsRef.current.keys()]) {
      if (!live.has(nodeId)) {
        manualPositionsRef.current.delete(nodeId);
      }
    }
  }, [graphFlow.nodes]);

  // Which nodes hold a ref that no longer reads anything, for the marks on
  // their cards. One pass over the graph; the node cards only look themselves up.
  const refIssues = useWorkflowRefIssues();
  // Wiring mistakes (a group-by with no for-each below it), computed from the
  // graph alone — cheap, so a sibling of the ref pass rather than folded into
  // it.
  const structureIssues = useWorkflowStructureIssues();

  /**
   * The positions the *layout* produced, which is what a re-fit answers to.
   *
   * Read off `graphFlow` rather than off the live `nodes` state, which is the
   * same list with the user's drags applied to it. Keyed on that, every frame
   * of a drag was a new key: the effect re-ran, `fitView` re-ran with it, and
   * the canvas zoomed out as the dragged node stretched the bounding box and
   * back in as it returned — for the whole duration of the drag.
   *
   * A drag never reaches here. It is held in `manualPositionsRef` and merged in
   * at `setNodes`; the layout it overrides is unchanged, so nothing re-fits
   * until the graph is laid out again.
   */
  const layoutKey = useMemo(
    () =>
      graphFlow.nodes
        .map(
          (node) =>
            `${node.id}:${node.position.x.toFixed(0)},${node.position.y.toFixed(
              0
            )}`
        )
        .join('|'),
    [graphFlow.nodes]
  );

  useEffect(() => {
    const selection = {
      selectedNodeIds: nodes
        .filter((node) => node.selected === true)
        .map((node) => node.id),
      selectedEdgeIds: edges
        .filter((edge) => edge.selected === true)
        .map((edge) => edge.id),
    };

    model.setEditorSelection(selection);

    syncWorkflowSelectionTargetModel({
      selection,
      graph,
      catalog,
      workflowModel: model,
      actions: actionsContext,
    });
  }, [nodes, edges, model, graph, catalog, actionsContext]);

  useEffect(() => {
    isHydratingRef.current = true;
    setNodes((current) => {
      const next = applyWorkflowFlowManualPositions(
        graphFlow.nodes,
        manualPositionsRef.current
      );
      // The clipboard actions leave the copies here for exactly one sync — this
      // is the one that renders them.
      const pastedIds = model.takePastedNodeIds();
      if (pastedIds !== null) {
        const withPasted = withPastedSelection(next, pastedIds);
        if (withPasted !== null) {
          return withPasted;
        }
      }
      return withPreservedSelection(current, next);
    });
    const frame = requestAnimationFrame(() => {
      isHydratingRef.current = false;
    });
    return () => {
      cancelAnimationFrame(frame);
    };
  }, [graphFlow.nodes, setNodes, model]);

  // Edges track either the plain graph or the test-run overlay (which updates
  // each poll). setEdges replaces state directly, so it never trips the
  // onEdgesChange "remove" path that deletes persisted edges.
  useEffect(() => {
    setEdges((current) => withPreservedSelection(current, displayEdges));
  }, [displayEdges, setEdges]);

  // Run the animation + "Testing…" label for a fixed window, then reset both.
  useEffect(() => {
    if (!isTesting) {
      return;
    }
    const timeout = setTimeout(() => setIsTesting(false), TEST_ANIMATION_MS);
    return () => clearTimeout(timeout);
  }, [isTesting]);

  const onNodesChangeWrapped = useCallback(
    (changes: NodeChange<Node>[]) => {
      if (isHydratingRef.current === false && mutations.canEdit === true) {
        for (const change of changes) {
          if (change.type === 'position' && change.position !== undefined) {
            if (
              change.dragging === false &&
              isWorkflowTriggerFlowNodeId(change.id) === false
            ) {
              manualPositionsRef.current.set(change.id, change.position);
            }
            continue;
          }
          if (change.type !== 'remove') {
            continue;
          }
          manualPositionsRef.current.delete(change.id);
          if (isWorkflowTriggerFlowNodeId(change.id)) {
            const node = nodes.find((n) => n.id === change.id);
            const bindingId = (
              node?.data as WorkflowFlowTriggerData | undefined
            )?.bindingId;
            if (bindingId !== undefined) {
              void mutations.archiveTriggerBinding(bindingId);
            }
            continue;
          }
          void mutations.deleteWorkflowNode(change.id);
        }
      }
      onNodesChange(changes);
    },
    [nodes, onNodesChange, mutations]
  );

  const onEdgesChangeWrapped = useCallback(
    (changes: EdgeChange<Edge>[]) => {
      if (isHydratingRef.current === false && mutations.canEdit === true) {
        for (const change of changes) {
          if (change.type !== 'remove') {
            continue;
          }
          if (isSyntheticWorkflowEdgeId(change.id)) {
            continue;
          }
          void mutations.deleteWorkflowEdge(change.id);
        }
      }
      onEdgesChange(changes);
    },
    [onEdgesChange, mutations]
  );

  const onConnect = useCallback(
    (connection: Connection) => {
      const { source, target } = connection;
      if (source === null || target === null) {
        return;
      }
      if (mutations.canEdit === false) {
        return;
      }
      if (
        isWorkflowTriggerFlowNodeId(source) ||
        isWorkflowTriggerFlowNodeId(target)
      ) {
        toast.error(
          'Connect workflow nodes only — links from triggers are automatic'
        );
        return;
      }
      void mutations.addWorkflowEdge(source, target);
    },
    [mutations]
  );

  const onReconnect = useCallback(
    (oldEdge: Edge, newConnection: Connection) => {
      const { source, target } = newConnection;
      if (source === null || target === null) {
        return;
      }
      if (mutations.canEdit === false) {
        return;
      }
      if (isSyntheticWorkflowEdgeId(oldEdge.id)) {
        toast.error('Trigger links are automatic and cannot be reassigned');
        return;
      }
      if (
        isWorkflowTriggerFlowNodeId(source) ||
        isWorkflowTriggerFlowNodeId(target)
      ) {
        toast.error(
          'Connect workflow nodes only — links from triggers are automatic'
        );
        return;
      }
      if (source === target) {
        toast.error('Cannot connect a node to itself');
        return;
      }
      setEdges((currentEdges) =>
        reconnectEdge(oldEdge, newConnection, currentEdges)
      );
      void mutations.updateWorkflowEdge(oldEdge.id, {
        fromNodeId: source,
        toNodeId: target,
      });
    },
    [mutations, setEdges]
  );

  const onReconnectStart = useCallback(() => {
    isReconnectingRef.current = true;
  }, []);

  // Every delete route converges here: the Backspace/Delete key, and the toolbar
  // button (which deletes through `deleteElements`). React Flow awaits this before
  // removing anything, so returning false cancels cleanly — nothing to undo, and no
  // flash of a node that comes back.
  //
  // It also hands us the edges connected to the doomed nodes, not just selected
  // ones, which is what lets the dialog say how much of the graph goes with it.
  const confirmDelete = useConfirmWorkflowDelete();
  const onBeforeDelete = useCallback(
    async ({
      nodes: nodesToDelete,
      edges: edgesToDelete,
    }: {
      nodes: Node[];
      edges: Edge[];
    }): Promise<boolean> => {
      if (mutations.canEdit === false) {
        return false;
      }
      return confirmDelete({ nodes: nodesToDelete, edges: edgesToDelete });
    },
    [confirmDelete, mutations.canEdit]
  );

  const onReconnectEnd = useCallback(() => {
    isReconnectingRef.current = false;
  }, []);

  const onConnectEnd = useCallback(
    (event: MouseEvent | TouchEvent, connectionState: FinalConnectionState) => {
      if (mutations.canEdit === false) {
        return;
      }
      const resolution = resolveConnectEnd(event, connectionState, {
        isReconnecting: isReconnectingRef.current,
      });
      if (resolution.action === 'add-node') {
        openAddNodeModal(connectFromForDroppedSource(resolution.fromNodeId));
        return;
      }
      if (resolution.action === 'connect') {
        onConnect({
          source: resolution.fromNodeId,
          target: resolution.toNodeId,
          sourceHandle: null,
          targetHandle: null,
        });
      }
    },
    [mutations.canEdit, onConnect, openAddNodeModal]
  );

  // Clicking a card opens its config, the way every other workflow builder
  // behaves. React Flow suppresses the click after a real drag (see
  // `nodeClickDistance` on the canvas), so repositioning a node never opens this.
  const onNodeClick = useCallback(
    (_event: React.MouseEvent, node: Node) => {
      if (node.type === WORKFLOW_FLOW_TRIGGER_TYPE) {
        const data = node.data as WorkflowFlowTriggerData;
        openConfig(
          { kind: 'trigger', data },
          workflowTriggerFlowNodeId(data.trigger.id)
        );
        return;
      }
      if (node.type === WORKFLOW_FLOW_NODE_TYPE) {
        const { node: viewData } = node.data as WorkflowFlowNodeData;
        openConfig({ kind: 'node', node: viewData }, node.id);
      }
    },
    [openConfig]
  );

  // The "+" on a card's source port. Same destination as dropping a connection
  // drag on empty pane (see `onConnectEnd` above) — this just makes it findable
  // without having to discover the drag first.
  const addNodeFromSource = useCallback<WorkflowAddNodeFromSource>(
    (flowNodeId, direction) => {
      if (mutations.canEdit === false) {
        return;
      }
      if (direction === 'upstream') {
        // A trigger has no input port, so `upstreamOf` can never name one —
        // nothing to guard here the way `connectFromForDroppedSource` does.
        openAddNodeModal({ upstreamOf: flowNodeId });
        return;
      }
      openAddNodeModal(connectFromForDroppedSource(flowNodeId));
    },
    [mutations.canEdit, openAddNodeModal]
  );

  return (
    <div className="relative h-full w-full">
      {/* Outside the flow so it is computed once for the graph, not once per
          node — every node card reads its own row out of it. */}
      <WorkflowRefIssuesProvider issues={refIssues}>
        <WorkflowStructureIssuesProvider issues={structureIssues}>
          <WorkflowAddNodeFromSourceProvider value={addNodeFromSource}>
            <WorkflowOpenNodeConfigProvider value={openNodeConfigById}>
              <Canvas
                controlsSlot={<WorkflowHistoryControls />}
                nodes={renderNodes}
                edges={renderEdges}
                onNodesChange={onNodesChangeWrapped}
                onEdgesChange={onEdgesChangeWrapped}
                onConnect={onConnect}
                onConnectEnd={onConnectEnd}
                onNodeClick={onNodeClick}
                onBeforeDelete={onBeforeDelete}
                // Default is 0, which drops the click after a single pixel of
                // trackpad jitter. A few pixels of slack keeps clicking reliable
                // while still suppressing genuine drags.
                nodeClickDistance={4}
                onReconnect={onReconnect}
                onReconnectStart={onReconnectStart}
                onReconnectEnd={onReconnectEnd}
                nodeTypes={WORKFLOW_NODE_TYPES}
                defaultEdgeOptions={WORKFLOW_EDGE_OPTIONS}
                className="h-full w-full"
                proOptions={{ hideAttribution: true }}
                nodesDraggable={mutations.canEdit}
                nodesConnectable={mutations.canEdit}
                edgesReconnectable={mutations.canEdit}
                elementsSelectable={true}
                // Shift- or Cmd-click adds nodes to the selection (React Flow's Mac
                // default is Meta only). Selecting several then "Add node" fans them
                // all into the new node.
                multiSelectionKeyCode={['Meta', 'Shift']}
                panOnDrag={true}
                panOnScroll
                selectionOnDrag={false}
                fitView={false}
                fitViewOptions={WORKFLOW_FLOW_FIT_VIEW_OPTIONS}
              >
                <WorkflowFitView layoutKey={layoutKey} />
                <WorkflowEditorToolbar
                  onTestStarted={handleTestStarted}
                  isTesting={isTesting}
                  toolbarSlot={toolbarSlot}
                  openAddNodeModal={openAddNodeModal}
                  onOpenNestedTree={openNestedTree}
                />
                <WorkflowEditorInspector
                  target={inspectorTarget}
                  onClose={closeInspector}
                />
              </Canvas>
            </WorkflowOpenNodeConfigProvider>
          </WorkflowAddNodeFromSourceProvider>
        </WorkflowStructureIssuesProvider>
      </WorkflowRefIssuesProvider>
    </div>
  );
};

export const WorkflowEditorCanvas: React.FC<{
  toolbarSlot: HTMLDivElement | null;
}> = ({ toolbarSlot }) => (
  <div className="h-full w-full min-h-0">
    <ReactFlowProvider>
      <WorkflowEditorCanvasInner toolbarSlot={toolbarSlot} />
    </ReactFlowProvider>
  </div>
);
